import { useEffect, useState } from "react";
import { dispatchCtxChange } from "@/components/add-v2/ui";
import logger from "@/console";

export function getDynamicValue(ctx, path) {
  if (!path?.includes(".")) return ctx[path];
  return path.split(".").reduce((acc, curr) => acc?.[curr], ctx);
}

const NumberFormat = new Intl.NumberFormat("en-SA", {
  maximumFractionDigits: 2,
});
function getDynamicRule({
  value,
  ctx,
  multiply,
  divide,
  plus,
  minus,
  ...rest
}) {
  let val = value;

  if (val && Number.isNaN(+val) && rest?.[val]) {
    return rest[val];
  }
  if (multiply) {
    if (multiply?.path) val = ctx[multiply?.path] * val;
    else val = multiply * val;
  }
  if (divide) {
    if (divide?.path) val /= ctx[divide.path];
    else val /= divide;
  }
  if (plus) {
    if (plus?.path) val += ctx[plus?.path];
    else val += plus;
  }
  if (minus) {
    if (minus?.path) val -= ctx[minus?.path];
    else val -= minus;
  }
  return val;
}

export function getDynamicLabel({ ctx, label }) {
  const key = ctx[label.path];
  if (!key) return label["default"] || "";
  return label ? label[key] : "";
}

export function doCalculations({ name, calculations, ctx }) {
  for (const item of calculations) {
    const { operation, operands } = item;

    if (!operands.length > 1) continue;
    let operand1 = +operands[0];
    let operand2 = +operands[1];
    ctx.recalculate = true;
    if (Number.isNaN(operand1)) {
      operand1 = ctx[operands[0]];
    }
    if (Number.isNaN(operand2)) {
      operand2 = ctx[operands[1]];
    }
    if (operand2 && operand1 && operation === "division") {
      ctx[name] = Math.round(operand1 / operand2);
    }
    if (operand2 && operand1 && operation === "summation") {
      ctx[name] = Math.round(operand1 + operand2);
    }
    if (operand2 && operand1 && operation === "subtraction") {
      ctx[name] = Math.round(operand1 - operand2);
    }
    if (operand2 && operand1 && operation === "multiplication") {
      ctx[name] = Math.round(operand1 * operand2);
    }
  }

  return ctx;
}

export function useRevalidateAndCalculations({
  rules,
  calculations,
  name,
  label,
  setError,
  dynamicProps,
  ctxRef,
  setValue,
  dependencies,
}) {
  const ctx = ctxRef?.current || {};
  const [props, setProps] = useState(dynamicProps?.[ctx?.[dynamicProps?.path]]);
  useEffect(() => {
    const revalidateMap =
      dependencies?.reduce((acc, curr) => ({ ...acc, [curr]: true }), {}) || {};

    function handleCtxChange(e) {
      const ctx = ctxRef?.current || {};

      const { name: fName, value: fValue } = e.detail || {};

      if (dynamicProps?.path === fName) setProps(dynamicProps[fValue]);

      if (!revalidateMap[fName]) return;

      const newCtx = calculations?.length
        ? doCalculations({
            calculations,
            name,
            ctx: { ...ctx, [fName]: fValue, recalculate: false },
          })
        : { ...ctx, [fName]: fValue };

      if (newCtx[name] !== ctx[name]) {
        dispatchCtxChange(e.target, {
          name,
          ctx: newCtx,
          value: newCtx[name],
        });
      }

      if (newCtx.recalculate && !newCtx[name]) return;

      setValue(newCtx[name]);
      const message = validate({
        rules,
        ctx: newCtx,
        recalculated: newCtx.recalculate,
        currentValue: newCtx[name],
        label,
        fName,
        fValue,
        name,
      });
      logger.log("[validation] useRevalidateAndCalculations", {
        name,
        message,
        changed: fName,
        value: fValue,
        newCtx,
        ctx,
      });

      setError(message);
    }

    document.addEventListener("ctxchange", handleCtxChange);
    return () => {
      document.removeEventListener("ctxchange", handleCtxChange);
    };
  }, [calculations, dynamicProps, dependencies, rules]);

  return props;
}

export function validate({
  rules = [],
  ctx,
  currentValue: cv,
  label,
  recalculated,
  fieldName,
}) {
  if (!rules) return false;

  let hasMissingRequiredField = false;
  if (fieldName === "premiums") {
    const { price_2_payments, price_4_payments, price_12_payments, price_2_payments_enabled, price_4_payments_enabled, price_12_payments_enabled } = ctx;

    if ((price_2_payments_enabled && !price_2_payments) || (price_4_payments_enabled && !price_4_payments) || (price_12_payments_enabled && !price_12_payments)) {
      hasMissingRequiredField = true;
    }

    rules[0].required = hasMissingRequiredField;
  }

  for (const rule of rules) {
    let {
      basedOn,
      min,
      minLength,
      max,
      equal,
      truthy,
      required,
      dynamic,
      path,
      message: m,
    } = rule;
    let message = m?.ar || "";
    let hasError = false;
    const currentValue = basedOn ? getDynamicValue(ctx, basedOn) : cv;

    if (!recalculated && required && !currentValue) {
      hasError = "required";

      return message.replaceAll("$name", label || "الحقل");
    }
    if (!hasError && dynamic && currentValue) {
      if (min)
        min = getDynamicRule({
          ...min,
          ctx,
          value: getDynamicValue(ctx, [path || min.path]),
        });
      if (max)
        max = getDynamicRule({
          ...max,
          ctx,
          value: ctx[path || max.path],
        });

      if (equal)
        equal = getDynamicRule({
          ...equal,
          ctx,
          value: ctx[path || equal.path],
        });

      if (truthy) {
        hasError = !getDynamicValue(ctx, path);
      }
    }
    if (!hasError && currentValue) {
      if ((min && +currentValue < +min) || !currentValue) {
        hasError = "min";
      }
      if (max && +currentValue > +max) {
        hasError = "max";
      }
      if (minLength && (currentValue + "").length < minLength) {
        hasError = "minLength";
      }
      if (equal && currentValue + "" !== equal + "") {
        hasError = "equal";
      }
    }

    logger.log("[validation] validate", {
      hasError,
      message,
      currentValue,
      min,
      max,
      equal,
      minLength,
      rule,
    });
    if (hasError) {
      return message
        .replaceAll("$max", max ? NumberFormat.format(max) : "")
        .replaceAll("$equal", equal ? NumberFormat.format(equal) : "")
        .replaceAll(
          "$minLength",
          minLength ? NumberFormat.format(minLength) : "",
        )
        .replaceAll("$min", min ? NumberFormat.format(min) : "")
        .replaceAll("$name", label || "الحقل");
    } else null;
  }
}
